// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import Truncate from "@rc/App/Components/Truncate/Truncate";
import buildProductDescriptionDataLayerEvent from "@rc/App/Handlers/GoogleAnalytics/ProductDescription/ProductDescriptionClick";
import { XngProductContextModel } from "@rc/Overrides/Widgets/ProductList/ProductListProductContext";
import { color } from "@rc/XDL/Utilities";
import parse from "html-react-parser";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    buildProductDescriptionDataLayerEvent,
};

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;

    // xng-change GCG-393 - pass the index in case we don't have productContext
    index?: number;
    truncate?: boolean;
}

type Props = OwnProps & HasProductContext & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        typographyProps: {
            css: css`
                &&&& {
                    width: 100%;
                    font-size: 18px;
                    line-height: 25px;
                    color: ${color("common.ui6")};
                    margin-bottom: 8px;
                    ${wrapInContainerStyles}
                    sup {
                        vertical-align: baseline;
                    }
                }
            `,
        },
        css: css`
            &.truncate span[class^="Truncated"] {
                max-height: 46px;
            }

            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: normal;
        `,
    },
    productDetailDescription: {
        css: css`
            &&&& {
                font-size: 18px;
                line-height: 25px;
                color: ${color("common.ui6")};
            }
        `,
    },
};

const ProductDescription = ({
    product,
    onClick,
    extendedStyles,
    buildProductDescriptionDataLayerEvent,
    productContext,
    index,
    truncate,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productDescriptionStyles, extendedStyles));

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    const handleClick = (event: React.MouseEvent) => {
        let productDescriptionObject = productContext;

        if (!Object.keys(productDescriptionObject).length) {
            productDescriptionObject = {
                product: product as any,
                productInfo: {} as ProductInfo,
                index,
            } as XngProductContextModel;
        }
        buildProductDescriptionDataLayerEvent(productDescriptionObject as any);
        onClick?.(event);
    };

    //  xng-change: use parse
    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Link
            {...styles.productDetailLink}
            href={productDetailPath}
            alt={`See product details for ${description}`}
            onClick={handleClick}
            data-test-selector="productDescriptionLink"
            className={truncate ? "truncate" : ""}
        >
            {truncate ? (
                <Truncate lines={2} lineHeight={18}>
                    {parse(description)}
                </Truncate>
            ) : (
                <> {parse(description)} </>
            )}
        </Link>
    ) : (
        <Typography {...styles.productDetailDescription} onClick={handleClick}>
            {parse(description)}
        </Typography>
    );
};

export default withProductContext(connect(null, mapDispatchToProps)(ProductDescription));
